import { tariffs } from '@/data/tariffs';
import { clamp } from '@/lib/M';
import { localeStore } from '@/locale';
import { formatPrice } from '@/utils/formatPrice';
import { debounce } from '@/utils/debounce';
import { trackEvent, tariffItemId } from '@/utils/analytics';
import { atom } from 'nanostores';

// Checkbox values as named in the GA4 spec (ARCH1-9617).
const OPTION_NAMES = {
  '2d': '2d',
  '3d': '3d_isometric',
  360: '360_tour',
  whiteLabel: 'white_label',
  multiStyle: 'multi_style',
  renderings: 'renderings',
};

export class CalcStore {
  $isOpened = atom(false);

  $checkedList = atom(new Set(['2d']));

  $floorplanCount = atom('');

  $recommendedName = atom('');

  $recommendedPrice = atom('');

  $totalPrice = atom('');

  floorplanCountValue = 0;

  recommendedSet = null;

  lastResultKey = null;

  location = 'homepage_pricing';

  store;

  constructor(store) {
    this.store = store;
  }

  init() {
    this.calculateRecommendedSet();
  }

  open(location = 'homepage_pricing') {
    this.location = location;
    this.$isOpened.set(true);
    this.store.scrollStore.lock('calc');
    trackEvent('calculator_open', {
      calculator_id: 'visual_set_calculator',
      calculator_location: location,
    });
  }

  close() {
    this.$isOpened.set(false);
    this.store.scrollStore.unlock('calc');
    this.resetForm();
  }

  check(value) {
    const list = new Set([...this.$checkedList.get()]);
    if (!list.has(value)) {
      this.fixTariffConflicts(list, value);
      list.add(value);
      this.$checkedList.set(list);
      this.trackOptionSelect(value, 'selected');
      this.calculateRecommendedSet();
    }
  }

  fixTariffConflicts(list, value) {
    if (value === 'multiStyle') list.delete('renderings');
    else if (value === 'renderings') list.delete('multiStyle');
  }

  uncheck(name) {
    const list = new Set([...this.$checkedList.get()]);
    if (list.has(name)) {
      list.delete(name);
      this.$checkedList.set(list);
      this.trackOptionSelect(name, 'unselected');
      this.calculateRecommendedSet();
    }
  }

  trackOptionSelect(value, state) {
    trackEvent('calculator_option_select', {
      option_name: OPTION_NAMES[value] || value,
      option_state: state,
    });
  }

  toggle(name) {
    const list = this.$checkedList.get();
    if (list.has(name)) {
      this.uncheck(name);
    } else {
      this.check(name);
    }
  }

  setFloorplanCount(value) {
    const clampedValue = clamp(value, 0, 5000);
    this.floorplanCountValue = clampedValue;
    this.$floorplanCount.set(value ? `${clampedValue}` : '');
    this.trackQuantitySet();
    this.calculateRecommendedSet();
  }

  // Debounced: the spec asks for one event per accepted value, not per keystroke.
  trackQuantitySet = debounce(() => {
    if (!this.floorplanCountValue) return;
    trackEvent('calculator_quantity_set', { floorplan_count: this.floorplanCountValue });
  }, 800);

  // Fires when the result block and the "Try now" button become visible. Debounced so a burst
  // of recalculations sends one event, deduped so the same recommendation is not repeated.
  trackResultView = debounce(() => this.reportResultView(), 800);

  // Call directly before leaving the calculator: a pending debounced call would be dropped.
  reportResultView() {
    if (!this.$isOpened.get() || !this.floorplanCountValue || !this.recommendedSet) return;

    const key = `${this.recommendedSet.name}:${this.floorplanCountValue}`;
    if (key === this.lastResultKey) return;
    this.lastResultKey = key;

    trackEvent('calculator_result_view', {
      floorplan_count: this.floorplanCountValue,
      recommended_item_id: tariffItemId(this.recommendedSet),
      recommended_item_name: this.recommendedSet.name,
      unit_price: this.recommendedSet.price,
      estimated_value: this.recommendedSet.price * this.floorplanCountValue,
      currency: 'USD',
    });
  }

  resetForm() {
    this.$checkedList.set(new Set(['2d']));
    this.$floorplanCount.set('');
    // Clearing only the displayed atom left the calculator recommending a hidden old result.
    this.floorplanCountValue = 0;
    this.lastResultKey = null;
    this.calculateRecommendedSet();
  }

  calculateRecommendedSet() {
    const floorplanCount = this.floorplanCountValue;
    if (!floorplanCount) return;

    const required = [...this.$checkedList.get()];
    const allTariffs = tariffs[localeStore.lang];
    const filteredTariffs = allTariffs.filter((tariff) => {
      const features = new Set();
      tariff.features.forEach((row) => {
        row.forEach((item) => {
          features.add(item.value);
        });
      });
      return required.every((item) => features.has(item));
    });

    if (filteredTariffs.length === 0) {
      this.recommendedSet.set(allTariffs[0]);
    } else {
      filteredTariffs.sort((a, b) => a.price - b.price);
      this.recommendedSet = filteredTariffs[0];
    }

    const total = this.recommendedSet.price * floorplanCount;
    const totalText = formatPrice(total);

    this.$recommendedName.set(this.recommendedSet.name);
    this.$recommendedPrice.set(localeStore.get('pricePostfix', this.recommendedSet.price));
    this.$totalPrice.set(localeStore.get('pricePrefix', totalText));

    this.trackResultView();
  }
}
